import React from 'react';

const AmortizationTable = ({ scheduleData, showYearlySummary = true }) => {
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return '$0.00';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateInput) => {
    if (!dateInput) return new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });

    let dateObj;
    if (typeof dateInput === 'string') {
      if (dateInput.includes('T') || dateInput.includes('-')) {
        const dateParts = dateInput.split('T')[0].split('-');
        if (dateParts.length === 3) {
          dateObj = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
        } else {
          dateObj = new Date(dateInput);
        }
      } else {
        dateObj = new Date(dateInput);
      }
    } else {
      dateObj = new Date(dateInput);
    }

    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(dateObj);
  };

  if (!Array.isArray(scheduleData) || scheduleData.length === 0) {
    return (
      <div className="bg-white">
        <h3 className="text-xl font-bold text-gray-900 mb-4 print:text-base">Payment Schedule</h3>
        <p className="text-gray-500 text-center py-8">No payment schedule available</p>
      </div>
    );
  }

  const groupedByYear = scheduleData.reduce((acc, payment) => {
    const year = new Date(payment.paymentDate).getFullYear();
    if (!acc[year]) {
      acc[year] = [];
    }
    acc[year].push(payment);
    return acc;
  }, {});

  const calculateYearlyTotals = (yearPayments) => {
    return yearPayments.reduce((totals, payment) => ({
      totalPayment: totals.totalPayment + (payment.paymentAmount || 0),
      totalInterest: totals.totalInterest + (payment.interestPayment || 0),
      totalPrincipal: totals.totalPrincipal + (payment.principalPayment || 0)
    }), { totalPayment: 0, totalInterest: 0, totalPrincipal: 0 });
  };

  const calculateRunningTotals = (upToYear) => {
    const years = Object.keys(groupedByYear).filter(year => parseInt(year) <= upToYear);
    return years.reduce((totals, year) => {
      const yearTotals = calculateYearlyTotals(groupedByYear[year]);
      return {
        totalPayment: totals.totalPayment + yearTotals.totalPayment,
        totalInterest: totals.totalInterest + yearTotals.totalInterest,
        totalPrincipal: totals.totalPrincipal + yearTotals.totalPrincipal
      };
    }, { totalPayment: 0, totalInterest: 0, totalPrincipal: 0 });
  };

  // Build a sequential payment number lookup keyed by paymentNumber
  const paymentSequence = {};
  scheduleData.forEach((payment, idx) => {
    paymentSequence[payment.paymentNumber] = idx + 1;
  });

  return (
    <div className="bg-white">
      <div className="mb-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4 print:text-base">Payment Schedule</h3>

        <div className="overflow-x-auto print:overflow-visible">
          <table className="w-full border-collapse border border-gray-300 print:border-gray-400 print:text-xs">
            <thead>
              <tr className="bg-primary text-white print:bg-gray-800">
                <th className="border border-gray-300 px-3 py-2 text-center text-sm font-semibold print:border-gray-400 print:px-1 print:py-1 print:text-xs">
                  #
                </th>
                <th className="border border-gray-300 px-3 py-2 text-left text-sm font-semibold print:border-gray-400 print:px-1 print:py-1 print:text-xs">
                  Date
                </th>
                <th className="border border-gray-300 px-3 py-2 text-right text-sm font-semibold print:border-gray-400 print:px-1 print:py-1 print:text-xs">
                  Payment
                </th>
                <th className="border border-gray-300 px-3 py-2 text-right text-sm font-semibold print:border-gray-400 print:px-1 print:py-1 print:text-xs">
                  Interest
                </th>
                <th className="border border-gray-300 px-3 py-2 text-right text-sm font-semibold print:border-gray-400 print:px-1 print:py-1 print:text-xs">
                  Principal
                </th>
                <th className="border border-gray-300 px-3 py-2 text-right text-sm font-semibold print:border-gray-400 print:px-1 print:py-1 print:text-xs">
                  Balance
                </th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(groupedByYear).map(([year, yearPayments]) => (
                <React.Fragment key={year}>
                  {yearPayments.map((payment, index) => (
                    <tr
                      key={payment.paymentNumber}
                      className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} print:bg-white print:odd:bg-gray-50`}
                    >
                      <td className="border border-gray-300 px-3 py-2 text-sm text-center print:border-gray-400 print:px-1 print:py-0.5 print:text-xs">
                        {paymentSequence[payment.paymentNumber] ?? payment.paymentNumber}
                      </td>
                      <td className="border border-gray-300 px-3 py-2 text-sm print:border-gray-400 print:px-1 print:py-0.5 print:text-xs">
                        {formatDate(payment.paymentDate)}
                      </td>
                      <td className="border border-gray-300 px-3 py-2 text-sm text-right font-mono print:border-gray-400 print:px-1 print:py-0.5 print:text-xs">
                        {formatCurrency(payment.paymentAmount || 0)}
                      </td>
                      <td className="border border-gray-300 px-3 py-2 text-sm text-right font-mono print:border-gray-400 print:px-1 print:py-0.5 print:text-xs">
                        {formatCurrency(payment.interestPayment || 0)}
                      </td>
                      <td className="border border-gray-300 px-3 py-2 text-sm text-right font-mono print:border-gray-400 print:px-1 print:py-0.5 print:text-xs">
                        {formatCurrency(payment.principalPayment || 0)}
                      </td>
                      <td className="border border-gray-300 px-3 py-2 text-sm text-right font-mono print:border-gray-400 print:px-1 print:py-0.5 print:text-xs">
                        {formatCurrency(payment.remainingBalance || 0)}
                      </td>
                    </tr>
                  ))}

                  {showYearlySummary && (
                    <>
                      {/* Yearly Totals Row */}
                      <tr className="bg-gray-200 font-semibold print:bg-gray-300">
                        <td className="border border-gray-300 px-3 py-2 text-sm text-center print:border-gray-400 print:px-1 print:py-0.5 print:text-xs">
                          —
                        </td>
                        <td className="border border-gray-300 px-3 py-2 text-sm print:border-gray-400 print:px-1 print:py-0.5 print:text-xs">
                          <strong>{year} Totals</strong>
                        </td>
                        <td className="border border-gray-300 px-3 py-2 text-sm text-right font-mono print:border-gray-400 print:px-1 print:py-0.5 print:text-xs">
                          <strong>{formatCurrency(calculateYearlyTotals(yearPayments).totalPayment)}</strong>
                        </td>
                        <td className="border border-gray-300 px-3 py-2 text-sm text-right font-mono print:border-gray-400 print:px-1 print:py-0.5 print:text-xs">
                          <strong>{formatCurrency(calculateYearlyTotals(yearPayments).totalInterest)}</strong>
                        </td>
                        <td className="border border-gray-300 px-3 py-2 text-sm text-right font-mono print:border-gray-400 print:px-1 print:py-0.5 print:text-xs">
                          <strong>{formatCurrency(calculateYearlyTotals(yearPayments).totalPrincipal)}</strong>
                        </td>
                        <td className="border border-gray-300 px-3 py-2 text-sm text-right print:border-gray-400 print:px-1 print:py-0.5 print:text-xs">
                          —
                        </td>
                      </tr>

                      {/* Running Totals Row */}
                      <tr className="bg-gray-300 font-semibold print:bg-gray-400">
                        <td className="border border-gray-300 px-3 py-2 text-sm text-center print:border-gray-400 print:px-1 print:py-0.5 print:text-xs">
                          —
                        </td>
                        <td className="border border-gray-300 px-3 py-2 text-sm print:border-gray-400 print:px-1 print:py-0.5 print:text-xs">
                          <strong>Running Total</strong>
                        </td>
                        <td className="border border-gray-300 px-3 py-2 text-sm text-right font-mono print:border-gray-400 print:px-1 print:py-0.5 print:text-xs">
                          <strong>{formatCurrency(calculateRunningTotals(parseInt(year)).totalPayment)}</strong>
                        </td>
                        <td className="border border-gray-300 px-3 py-2 text-sm text-right font-mono print:border-gray-400 print:px-1 print:py-0.5 print:text-xs">
                          <strong>{formatCurrency(calculateRunningTotals(parseInt(year)).totalInterest)}</strong>
                        </td>
                        <td className="border border-gray-300 px-3 py-2 text-sm text-right font-mono print:border-gray-400 print:px-1 print:py-0.5 print:text-xs">
                          <strong>{formatCurrency(calculateRunningTotals(parseInt(year)).totalPrincipal)}</strong>
                        </td>
                        <td className="border border-gray-300 px-3 py-2 text-sm text-right print:border-gray-400 print:px-1 print:py-0.5 print:text-xs">
                          —
                        </td>
                      </tr>
                    </>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AmortizationTable;
